import { Address } from "@/types/address.types";
import { ImportOptions, ImportResult, QAddrFileData, EXPORT_CONSTANTS } from "@/types/export.types";
import { addressRepository } from "@/db/repositories/address.repository";
import { CSVImportService } from "../csv/csv-import.service";
import { decompressData } from "@/utils/compression.utils";
import { decryptData } from "@/utils/crypto.utils";
import { chunkArray } from "@/utils/string.utils";
import { logRepository } from "@/db/repositories/log.repository";
import { db } from "@/db/db";

export class AddressImportService {
  private csvImportService = new CSVImportService();

  async importFile(
    file: File,
    options: ImportOptions,
    onProgress?: (progress: number, total: number) => void
  ): Promise<ImportResult> {
    const extension = file.name.split(".").pop()?.toLowerCase();

    if (extension === "csv") {
      return this.importCSV(file, options, onProgress);
    } else if (extension === "qaddr") {
      return this.importCustom(file, options, onProgress);
    } else {
      throw new Error("不支持的文件格式");
    }
  }

  private async importCSV(
    file: File,
    _options: ImportOptions,
    onProgress?: (progress: number, total: number) => void
  ): Promise<ImportResult> {
    const result = await this.csvImportService.importCSV(file, onProgress);
    return {
      successCount: result.successCount,
      failureCount: result.failureCount,
      duplicateCount: 0,
    };
  }

  private async importCustom(
    file: File,
    options: ImportOptions,
    onProgress?: (progress: number, total: number) => void
  ): Promise<ImportResult> {
    const fileBytes = await this.readFileAsArrayBuffer(file);
    const header = this.parseHeader(fileBytes);

    if (header.magic !== EXPORT_CONSTANTS.MAGIC_NUMBER) {
      throw new Error("无效的文件格式");
    }

    if (header.version !== EXPORT_CONSTANTS.VERSION) {
      throw new Error("不支持的文件版本");
    }

    const isEncrypted = (header.flags & EXPORT_CONSTANTS.FLAGS.ENCRYPTED) !== 0;
    const isCompressed = (header.flags & EXPORT_CONSTANTS.FLAGS.COMPRESSED) !== 0;

    let dataBytes = this.extractData(fileBytes, header.dataLength);

    if (isEncrypted) {
      if (!options.password) {
        throw new Error("文件已加密,请提供密码");
      }
      const encryptedData = this.extractEncryptedData(dataBytes);
      dataBytes = await decryptData(encryptedData, options.password);
    }

    if (isCompressed) {
      const decompressedJson = decompressData(dataBytes);
      dataBytes = new TextEncoder().encode(decompressedJson);
    }

    const jsonData = new TextDecoder().decode(dataBytes);
    const fileData: QAddrFileData = JSON.parse(jsonData) as QAddrFileData;

    return this.importAddresses(fileData.addresses, options, onProgress);
  }

  private parseHeader(bytes: Uint8Array): {
    magic: string;
    version: number;
    flags: number;
    dataLength: number;
  } {
    const magic = new TextDecoder().decode(bytes.slice(0, 4));
    const version = bytes[4] ?? 0;
    const flags = bytes[5] ?? 0;
    const dataLength = new DataView(bytes.buffer).getUint32(6, false);

    return { magic, version, flags, dataLength };
  }

  private extractData(bytes: Uint8Array, dataLength: number): Uint8Array {
    const headerSize = 10;
    return bytes.slice(headerSize, headerSize + dataLength);
  }

  private extractEncryptedData(bytes: Uint8Array): {
    encrypted: Uint8Array;
    salt: Uint8Array;
    iv: Uint8Array;
    authTag: Uint8Array;
  } {
    let offset = 0;

    const salt = bytes.slice(offset, offset + EXPORT_CONSTANTS.SALT_LENGTH);
    offset += EXPORT_CONSTANTS.SALT_LENGTH;

    const iv = bytes.slice(offset, offset + EXPORT_CONSTANTS.IV_LENGTH);
    offset += EXPORT_CONSTANTS.IV_LENGTH;

    const authTag = bytes.slice(
      offset,
      offset + EXPORT_CONSTANTS.AUTH_TAG_LENGTH
    );
    offset += EXPORT_CONSTANTS.AUTH_TAG_LENGTH;

    const encrypted = bytes.slice(offset);

    return { encrypted, salt, iv, authTag };
  }

  private async importAddresses(
    addresses: Address[],
    options: ImportOptions,
    onProgress?: (progress: number, total: number) => void
  ): Promise<ImportResult> {
    let successCount = 0;
    let failureCount = 0;
    let duplicateCount = 0;

    const existingAddresses = await addressRepository.findAll();
    const existingAddressMap = new Map<string, Address>();
    existingAddresses.forEach((addr) => {
      existingAddressMap.set(addr.call, addr);
    });

    const batches = chunkArray(addresses, 300);

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      if (!batch) continue;

      const processedAddresses: Address[] = [];

      for (const address of batch) {
        try {
          if (options.importValidOnly && address.status !== "valid") {
            continue;
          }

          const existingAddress = existingAddressMap.get(address.call);

          if (existingAddress) {
            if (options.overwriteDuplicates) {
              processedAddresses.push({
                ...existingAddress,
                ...address,
                updatedAt: new Date().toISOString(),
              });
              duplicateCount++;
            } else {
              duplicateCount++;
            }
          } else {
            processedAddresses.push({
              ...address,
              updatedAt: new Date().toISOString(),
            });
            successCount++;
          }
        } catch {
          failureCount++;
        }
      }

      try {
        await db.addresses.bulkPut(processedAddresses);
      } catch {
        failureCount += processedAddresses.length;
      }

      if (onProgress) {
        onProgress((i + 1) * 300, addresses.length);
      }
    }

    await logRepository.create({
      operation: "import",
      resource: "address",
      status: "success",
      details: `成功导入 ${successCount} 条地址，失败 ${failureCount} 条，跳过重复 ${duplicateCount} 条`,
    });

    return { successCount, failureCount, duplicateCount };
  }

  private readFileAsArrayBuffer(file: File): Promise<Uint8Array> {
    return new Promise<Uint8Array>((resolve: (value: Uint8Array) => void, reject: (reason?: unknown) => void) => {
      const reader = new FileReader();
      reader.onload = (): void => {
        const arrayBuffer = reader.result as ArrayBuffer;
        resolve(new Uint8Array(arrayBuffer));
      };
      reader.onerror = (): void => {
        reject(new Error("文件读取失败"));
      };
      reader.readAsArrayBuffer(file);
    });
  }
}

export const addressImportService = new AddressImportService();
